import { useState, useRef, useEffect } from "react";
import { Pencil, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface ReviewValueRowProps {
  label: string;
  value: string | number | undefined | null;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  type?: "text" | "number" | "tel";
  className?: string;
  /** If false, shows value as read-only without edit capability */
  editable?: boolean;
  /** For displaying formatted values (e.g., currency) */
  displayValue?: string;
  /** Additional element to show after the value (e.g., currency symbol) */
  suffix?: string;
}

/**
 * ReviewValueRow - Read-only field with inline edit capability
 * 
 * UX Spec:
 * - Default state: read-only value row (Label → Value)
 * - Click value → inline edit mode
 * - Autosave on blur/enter → returns to read-only
 * - Subtle edit affordance (pencil on hover)
 */
export function ReviewValueRow({
  label,
  value,
  onValueChange,
  placeholder,
  type = "text",
  className,
  editable = true,
  displayValue,
  suffix,
}: ReviewValueRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(String(value ?? ""));
  const [showSaved, setShowSaved] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync localValue when external value changes
  useEffect(() => {
    setLocalValue(String(value ?? ""));
  }, [value]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmed = localValue.trim();
    if (trimmed !== String(value ?? "")) {
      onValueChange?.(trimmed);
      // Show saved feedback
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 1000);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalValue(String(value ?? ""));
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleClick = () => {
    if (editable && !isEditing) {
      setIsEditing(true);
    }
  };

  const displayText = displayValue ?? String(value ?? "");
  const isEmpty = !value && value !== 0;

  return (
    <div
      className={cn(
        "flex items-center justify-between py-2.5 border-b border-border/50 last:border-b-0 group",
        className
      )}
    >
      <span className="text-sm text-muted-foreground shrink-0 mr-4">
        {label}
      </span>

      {isEditing ? (
        <div className="flex items-center gap-2 flex-1 justify-end">
          <Input
            ref={inputRef}
            type={type}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="h-8 text-right max-w-[200px]"
          />
          <button
            type="button"
            onClick={handleSave}
            className="p-1 hover:bg-primary/10 rounded text-primary"
            aria-label="Save"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="p-1 hover:bg-destructive/10 rounded text-muted-foreground"
            aria-label="Cancel"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div
          onClick={handleClick}
          className={cn(
            "flex items-center gap-2 text-right",
            editable && "cursor-pointer hover:bg-muted/50 rounded px-2 -mx-2 py-1 transition-colors"
          )}
        >
          {showSaved && (
            <Check className="h-3.5 w-3.5 text-primary animate-in fade-in duration-200" />
          )}
          <span
            className={cn(
              "font-medium",
              isEmpty && "text-muted-foreground italic"
            )}
          >
            {isEmpty ? placeholder || "—" : displayText}
            {suffix && !isEmpty && ` ${suffix}`}
          </span>
          {editable && (
            <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
      )}
    </div>
  );
}
